import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import { useTareas } from "../context/TareasContext"
import { useState, useEffect, useMemo } from "react"
import axios from "axios"
import { BACKEND } from "../../../config/configEnvs"
import formatWorkerDisplayName from "../utils/formatWorkerDisplayName"

const CalendarView = () => {
  const { selectedProject, openTask, openCreateModal, getTaskStatusColor, workers } = useTareas()

  const [calendarTasks, setCalendarTasks] = useState([])
  const [loading, setLoading] = useState(false)

  // Carga TODAS las tareas del proyecto, sin paginación ni filtro de estado
  useEffect(() => {
    if (!selectedProject?.id) return
    setLoading(true)
    axios
      .get(
        `${BACKEND}/api/tareasAdministracion?limit=0&depth=2` +
        `&where[proyecto][equals]=${encodeURIComponent(selectedProject.id)}`
      )
      .then(res => setCalendarTasks(res.data.docs || []))
      .catch(() => setCalendarTasks([]))
      .finally(() => setLoading(false))
  }, [selectedProject?.id])

  const events = useMemo(() =>
    calendarTasks.map(t => {
      const color = getTaskStatusColor(t)
      const assignees = t.assignees || []
      const names = assignees
        .map((a) => {
          if (typeof a === "object" && a?.name) return formatWorkerDisplayName(a.name)
          const id = typeof a === "object" ? a?.id : a
          const w = workers.find((x) => x.id === id)
          return formatWorkerDisplayName(w?.name || w?.email || String(id || ""))
        })
        .filter(Boolean)
        .join(", ")
      // Usa fecha límite si existe, si no, fecha de creación
      const eventDate = t.limitDate || t.createdAt
      return {
        id: t.id,
        title: t.task,
        date: eventDate,
        backgroundColor: t.limitDate ? color : "#9CA3AF",
        borderColor: t.limitDate ? color : "#9CA3AF",
        textColor: "#fff",
        extendedProps: { task: t, assigneeNames: names, hasDeadline: !!t.limitDate },
      }
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getTaskStatusColor depende internamente de estadosTareas
  [calendarTasks, getTaskStatusColor, workers])

  const handleEventClick = ({ event }) => {
    openTask(event.extendedProps.task)
  }

  const handleDateClick = () => {
    openCreateModal()
  }

  return (
    <div className="h-full p-4 overflow-auto relative">
      {loading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60">
          <span className="text-sm text-gray-400">Cargando eventos…</span>
        </div>
      )}
      <style>{`
        .fc { font-family: inherit; font-size: 12px; }
        .fc-toolbar-title { font-size: 16px !important; font-weight: 700; }
        .fc-button { font-size: 11px !important; padding: 4px 10px !important; }
        .fc-button-primary { background-color: #FE6400 !important; border-color: #FE6400 !important; }
        .fc-button-primary:hover { background-color: #bb4a03 !important; border-color: #bb4a03 !important; }
        .fc-button-primary:not(.fc-button-active):not(:disabled):hover { background-color: #bb4a03 !important; }
        .fc-button-active { background-color: #bb4a03 !important; }
        .fc-daygrid-event { border-radius: 4px !important; padding: 1px 4px !important; font-size: 10px !important; }
        .fc-event-title { font-weight: 600; }
        .fc-day-today { background-color: #FFF7ED !important; }
        .fc-col-header-cell { background-color: #F9FAFB; }
      `}</style>
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        }}
        locale="es"
        buttonText={{
          today: "Hoy",
          month: "Mes",
          week: "Semana",
          day: "Día",
        }}
        events={events}
        eventClick={handleEventClick}
        dateClick={handleDateClick}
        height="100%"
        eventDisplay="block"
        dayMaxEvents={4}
        eventMouseEnter={(info) => { info.el.style.cursor = "pointer" }}
        eventContent={(arg) => (
          <div className="flex items-center gap-1 overflow-hidden px-0.5">
            <span className="font-semibold truncate">{arg.event.title}</span>
            {arg.event.extendedProps.assigneeNames && (
              <span className="opacity-75 text-[9px] truncate">· {arg.event.extendedProps.assigneeNames}</span>
            )}
          </div>
        )}
      />
    </div>
  )
}

export default CalendarView
